import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../axios/axiosInstance';
import { notify } from '../../../../utils/notification';
import apiEndpoints from '../../../../axios/api';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import ViewVendorDetailsModal from './ViewVendorDetailsModal';
import EditVendorModal from './EditVendorModal';
import AuditLogModal from './AuditLogModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { usePageRefresh } from '../../../../context/PageRefreshContext';

const ManageVendorListLayer = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [societyFilter, setSocietyFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editVendor, setEditVendor] = useState(null);
    const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
    const [auditLogVendor, setAuditLogVendor] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [vendorsData, setVendorsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // Every society's vendors. The webmaster's token is a super admin, so
    // /vendors/all comes back unscoped; this page used to render a hardcoded
    // sample list, so vendors added in a society never showed up here.
    const loadVendors = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_GET_ALL_VENDORS_API);
            const rows = Array.isArray(res?.data) ? res.data : (res?.data?.rows || []);
            // Every field the details view shows, not just the table columns -
            // the modal used to print "N/A" for most of them.
            setVendorsData(rows.map((v) => ({
                id: v.vendor_id,
                name: v.business_name || '-',
                category: v.category?.label || '-',
                email: v.email || v.user?.email || '-',
                representative: v.contact_person || '-',
                phone: v.phone || '-',
                alternatePhone: v.alternate_phone || '',
                website: v.website || '',
                address: v.address || '',
                pincode: v.pincode || '',
                city: v.city?.name || v.city || '',
                society: v.society?.name || 'Shared',
                societyId: v.society_id ?? '',
                glCode: v.vendor_gl_code || '-',
                panNumber: v.pan_number || '',
                gstNumber: v.gst_number || '',
                tanNumber: v.tan_number || '',
                msmeStatus: v.msme_status || '',
                rcmApplicable: v.rcm_applicable === 'Y',
                tdsApplicable: v.tds_applicable === 'Y',
                paymentTerms: v.payment_terms || '',
                contractStatus: v.contract_status || '',
                contractStart: v.contract_start_date || '',
                contractEnd: v.contract_end_date || '',
                contractAmount: v.contract_amount ?? '',
                serviceDescription: v.service_description || v.notes || '',
                isVerified: Number(v.is_verified) === 1,
                createdAt: v.created_at || '',
                updatedAt: v.updated_at || '',
                status: Number(v.is_active) === 1,
                raw: v
            })));
        } catch (err) {
            setLoadError(err?.response?.data?.message || 'Could not load vendors');
            setVendorsData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadVendors(); }, [loadVendors]);
    usePageRefresh(loadVendors);

    // Society filter options come from the vendors on hand.
    const societiesList = useMemo(
        () => [...new Set(vendorsData.map((v) => v.society).filter(Boolean))].sort(),
        [vendorsData]
    );

    // Filtering logic
    const filteredVendors = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return vendorsData.filter(vendor => {
            const searchMatch = !q || [vendor.name, vendor.email, vendor.category, vendor.phone, vendor.society]
                .some((field) => String(field || '').toLowerCase().includes(q));
            const statusMatch = !statusFilter || (statusFilter === 'active' ? vendor.status : !vendor.status);
            const societyMatch = !societyFilter || vendor.society === societyFilter;
            return searchMatch && statusMatch && societyMatch;
        });
    }, [vendorsData, searchTerm, statusFilter, societyFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
    const paginatedVendors = filteredVendors.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Modal handlers
    const handleViewClick = (vendor) => {
        setSelectedVendor(vendor);
        setViewModalOpen(true);
    };

    const handleEditClick = (vendor) => {
        setEditVendor(vendor);
        setEditModalOpen(true);
    };

    const handleAuditLogClick = (vendor) => {
        setAuditLogVendor(vendor);
        setAuditLogModalOpen(true);
    };

    const handleDeleteClick = (vendor) => {
        setVendorToDelete(vendor);
        setDeleteModalOpen(true);
    };

    // Active / inactive, saved through the same update endpoint the society
    // portal uses. This only logged to the console before.
    const handleStatusChange = async (vendor) => {
        const next = !vendor.status;
        try {
            await axiosInstance.put(
                apiEndpoints.SOCIETY_UPDATE_VENDOR_API.replace(':id', vendor.id),
                { is_active: next ? 1 : 0 }
            );
            setVendorsData((prev) => prev.map((v) => (v.id === vendor.id ? { ...v, status: next } : v)));
            notify('success', `${vendor.name} is now ${next ? 'active' : 'inactive'}`);
        } catch (err) {
            notify('error', err?.response?.data?.message || 'Could not change the status');
        }
    };

    const handleClear = () => {
        setSearchTerm('');
        setSocietyFilter('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    const handleDeleteConfirm = async () => {
        if (!vendorToDelete) return;
        setIsDeleting(true);
        try {
            await axiosInstance.delete(apiEndpoints.SOCIETY_DELETE_VENDOR_API.replace(':id', vendorToDelete.id));
            notify('success', `${vendorToDelete.name} deleted`);
            setDeleteModalOpen(false);
            setVendorToDelete(null);
            loadVendors();
        } catch (error) {
            notify('error', error?.response?.data?.message || 'Could not delete the vendor');
        } finally {
            setIsDeleting(false);
        }
    };

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, filteredVendors.length);

    return (
        <div className="card">
            {/* Filter Section */}
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="tw-w-48">
                        <SearchableDropdown
                            value={societyFilter || 'all'}
                            onChange={(v) => { setSocietyFilter(v === 'all' ? '' : v); setCurrentPage(1); }}
                            options={[
                                { value: 'all', label: 'All Communities' },
                                ...societiesList.map((s) => ({ value: s, label: s })),
                            ]}
                            placeholder="Community"
                            clearable={false}
                            small
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={statusFilter || 'all'}
                            onChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setCurrentPage(1); }}
                            options={[
                                { value: 'all', label: 'All Status' },
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>

                    {/* Search by Name */}
                    <div className="icon-field">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search by vendor name"
                            className="form-control form-control-sm w-auto"
                        />
                        <span className="icon">
                            <Icon icon="ion:search-outline" />
                        </span>
                    </div>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3">
                    {/* Clear Button */}
                    <button
                        onClick={handleClear}
                        className="btn btn-sm"
                        style={{
                            backgroundColor: '#CCCCCC',
                            color: 'white',
                            border: '1px solid #AAAAAA',
                        }}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Table Section */}
            {/* The table outgrew the card once Community and GL were added, and the
                columns on the right (status, actions) were simply cut off with no
                way to reach them. table-responsive gives it a horizontal scrollbar;
                nowrap keeps cells from collapsing into unreadable stacks. */}
            <div className="card-body p-0 table-responsive" style={{ overflowX: 'auto' }}>
                <table className="table bordered-table mb-0" style={{ whiteSpace: 'nowrap', minWidth: '1100px' }}>
                    <thead>
                        <tr>
                            <th scope="col">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                    />
                                    <label className="form-check-label">
                                        #
                                    </label>
                                </div>
                            </th>
                            <th scope="col">Vendor Name</th>
                            <th scope="col">Community</th>
                            <th scope="col">Category</th>
                            <th scope="col">Email</th>
                            <th scope="col">Representative</th>
                            <th scope="col">Phone</th>
                            <th scope="col">City</th>
                            <th scope="col">Status</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedVendors.length > 0 ? (
                            paginatedVendors.map((vendor, index) => (
                                <tr key={vendor.id}>
                                    <td>
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                            />
                                            <label className="form-check-label">
                                                {startIndex + index}
                                            </label>
                                        </div>
                                    </td>
                                    <td className="text-md fw-medium">
                                        {vendor.name}
                                        {vendor.glCode && vendor.glCode !== '-' && (
                                            <div className="text-xs text-secondary-light">GL {vendor.glCode}</div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="text-xs text-gray-600">{vendor.society}</div>
                                    </td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {vendor.category}
                                        </div>
                                    </td>
                                    <td>{vendor.email}</td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {vendor.representative}
                                        </div>
                                    </td>
                                    <td>{vendor.phone}</td>
                                    <td>
                                        <div className="text-xs text-gray-600">
                                            {vendor.city}
                                        </div>
                                    </td>
                                    <td>
                                        <Switch
                                            checked={vendor.status}
                                            onChange={() => handleStatusChange(vendor)}
                                            size="small"
                                        />
                                    </td>
                                    <td>
                                        {/* Edit Button */}
                                        <Tooltip title="Edit" placement="top" arrow>
                                            <button
                                                onClick={() => handleEditClick(vendor)}
                                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </button>
                                        </Tooltip>

                                        {/* View Details Button */}
                                        <Tooltip title="View" placement="top" arrow>
                                            <button
                                                onClick={() => handleViewClick(vendor)}
                                                className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="iconamoon:eye-light" />
                                            </button>
                                        </Tooltip>

                                        {/* Audit Log Button */}
                                        <Tooltip title="Audit Log" placement="top" arrow>
                                            <button
                                                onClick={() => handleAuditLogClick(vendor)}
                                                className="w-32-px h-32-px me-8 bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="solar:clipboard-list-bold" />
                                            </button>
                                        </Tooltip>

                                        {/* Delete Button */}
                                        <Tooltip title="Delete" placement="top" arrow>
                                            <button
                                                onClick={() => handleDeleteClick(vendor)}
                                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </button>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="text-center py-4">
                                    <p className="text-gray-600">
                                        {loading ? 'Loading vendors…' : (loadError || 'No vendors found')}
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Section */}
            <div className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-3 ps-2">
                <div className="text-sm text-gray-600">
                    {filteredVendors.length > 0 ? (
                        <>
                            Showing {startIndex} to {endIndex} of {filteredVendors.length} vendors
                        </>
                    ) : (
                        'No vendors'
                    )}
                </div>
                <PaginationLimit
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={(value) => {
                        setItemsPerPage(value);
                        setCurrentPage(1);
                    }}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                />
            </div>

            {/* Modals */}
            <ViewVendorDetailsModal
                open={viewModalOpen}
                handleClose={() => {
                    setViewModalOpen(false);
                    setSelectedVendor(null);
                }}
                vendor={selectedVendor}
            />

            <EditVendorModal
                open={editModalOpen}
                handleClose={() => {
                    setEditModalOpen(false);
                    setEditVendor(null);
                }}
                vendor={editVendor}
                onSaved={() => { setEditModalOpen(false); loadVendors(); }}
            />

            <AuditLogModal
                open={auditLogModalOpen}
                handleClose={() => {
                    setAuditLogModalOpen(false);
                    setAuditLogVendor(null);
                }}
                vendor={auditLogVendor}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={() => {
                    setDeleteModalOpen(false);
                    setVendorToDelete(null);
                }}
                handleConfirm={handleDeleteConfirm}
                vendor={vendorToDelete}
                isLoading={isDeleting}
            />
        </div>
    );
};

export default ManageVendorListLayer;
